// formula-settings.js

// === Set this to 'prod' or 'dev' to switch formula API + image endpoints ===
const FORMULA_ENV = 'prod';
// =========================================================================

// Configuration
const CONFIG = {
    TEST_MODE: false,
    BASE_URL: FORMULA_ENV === 'prod'
        ? 'https://api.babybrezzacloud.com/formulas/finder'
        : 'https://dev-api.babybrezzacloud.com/formulas/finder',
    API_URL: FORMULA_ENV === 'prod'
        ? 'https://api.babybrezzacloud.com/formulas/finder'
        : 'https://dev-api.babybrezzacloud.com/formulas/finder',
    IMAGE_BASE: FORMULA_ENV === 'prod'
        ? 'https://babybrezzacloud.com/uploads/formulaprotool/original'
        : 'https://dev.babybrezzacloud.com/uploads/formulaprotool/original',
    DEBUG: false
};

// Cache Service
class CacheService {
    constructor() {
        this.cache = new Map();
    }

    async get(key, fetchFn, ttl = 300000) { // 5 minutes default TTL
        if (this.cache.has(key)) {
            const { data, timestamp } = this.cache.get(key);
            if (Date.now() - timestamp < ttl) {
                return data;
            }
        }

        const data = await fetchFn();
        this.cache.set(key, { data, timestamp: Date.now() });
        return data;
    }
}

// Utility Functions
const utils = {
    log: (...args) => {
        if (CONFIG.DEBUG) {
            // console.log('[Formula Settings]', ...args);
        }
    },

    error: (...args) => {
        if (CONFIG.DEBUG) {
            // console.error('[Formula Settings]', ...args);
        }
    },

    getCookie: (name) => {
        const value = `; ${document.cookie}`;
        const parts = value.split(`; ${name}=`);
        if (parts.length === 2) return parts.pop().split(';').shift();
        return '';
    },

    scrollToElement: (selector, offset = 0) => {
        const element = document.querySelector(selector);
        if (element) {
            const rect = element.getBoundingClientRect();
            const elementTop = rect.top + window.pageYOffset;
            window.scrollTo({
                top: elementTop + offset,
                behavior: 'smooth'
            });
        }
    },

    _toastLastMessage: '',
    _toastLastAt: 0,
    showToast: (message, type = 'warning') => {
        const now = Date.now();
        if (utils._toastLastMessage === message && (now - utils._toastLastAt) < 800) {
            return;
        }
        utils._toastLastMessage = message;
        utils._toastLastAt = now;

        const themes = {
            error:   { bg: '#f8d7da', text: '#6b1f24', accent: '#dc3545', icon: '!' },
            success: { bg: '#d4edda', text: '#1c5128', accent: '#28a745', icon: '✓' },
            warning: { bg: '#fff3cd', text: '#5b3a00', accent: '#f0a500', icon: '!' }
        };
        const theme = themes[type] || themes.warning;

        let container = document.getElementById('formula-settings-toast-container');
        if (!container) {
            container = document.createElement('div');
            container.id = 'formula-settings-toast-container';
            container.style.cssText = 'position:fixed;bottom:32px;left:50%;transform:translateX(-50%);z-index:2147483646;display:flex;flex-direction:column-reverse;gap:10px;pointer-events:none;max-width:92vw;';
            document.body.appendChild(container);
        }
        const toast = document.createElement('div');
        toast.style.cssText = 'display:flex;align-items:center;gap:12px;background:' + theme.bg + ';color:' + theme.text + ';padding:14px 22px 14px 18px;border-left:5px solid ' + theme.accent + ';border-radius:10px;box-shadow:0 10px 28px rgba(0,0,0,0.22),0 2px 6px rgba(0,0,0,0.08);font-size:15px;font-weight:600;line-height:1.4;max-width:100%;text-align:left;letter-spacing:0.1px;opacity:0;transform:translateY(12px);transition:opacity 220ms ease,transform 220ms ease;pointer-events:auto;';
        const icon = document.createElement('span');
        icon.setAttribute('aria-hidden', 'true');
        icon.style.cssText = 'flex:0 0 auto;display:inline-flex;align-items:center;justify-content:center;width:24px;height:24px;border-radius:50%;background:' + theme.accent + ';color:#fff;font-weight:700;font-size:15px;line-height:1;font-family:"Inter",system-ui,sans-serif;';
        icon.textContent = theme.icon;
        const text = document.createElement('span');
        text.style.cssText = 'flex:1 1 auto;';
        text.textContent = message;
        toast.appendChild(icon);
        toast.appendChild(text);
        container.appendChild(toast);
        requestAnimationFrame(() => {
            toast.style.opacity = '1';
            toast.style.transform = 'translateY(0)';
        });
        setTimeout(() => {
            toast.style.opacity = '0';
            toast.style.transform = 'translateY(8px)';
            setTimeout(() => toast.remove(), 220);
        }, 3000);
    },

    scrollToCTA: (selector = '.formula-settings-finder__cta') => {
        const element = document.querySelector(selector);
        if (!element) return;

        const active = document.activeElement;
        if (active && typeof active.blur === 'function' && (active.tagName === 'INPUT' || active.tagName === 'TEXTAREA' || active.tagName === 'SELECT')) {
            active.blur();
        }

        const doScroll = () => {
            const header = document.querySelector('sticky-header, .header[is=sticky-header], .shopify-section-header-sticky, #shopify-section-header');
            let headerOffset = 0;
            if (header) {
                const headerRect = header.getBoundingClientRect();
                const headerStyle = window.getComputedStyle(header);
                const isStickyOrFixed = headerStyle.position === 'fixed' || headerStyle.position === 'sticky' || document.body.classList.contains('header-pinned') || header.tagName.toLowerCase() === 'sticky-header';
                if (isStickyOrFixed && headerRect.height > 0) {
                    headerOffset = headerRect.height;
                }
            }

            const rect = element.getBoundingClientRect();
            const elementTop = rect.top + window.pageYOffset;
            const vvOffsetTop = window.visualViewport ? window.visualViewport.offsetTop : 0;
            const isMobile = window.matchMedia('(max-width: 749px)').matches;
            const gap = isMobile ? 40 : 60;

            window.scrollTo({
                top: elementTop - headerOffset - gap - vvOffsetTop,
                behavior: 'smooth'
            });
        };

        // Keyboard dismissal needs a tick before viewport metrics settle
        if (window.visualViewport && window.visualViewport.height < window.innerHeight - 100) {
            setTimeout(doScroll, 350);
        } else {
            doScroll();
        }
    }
};

function validEmail(email) {
    if (!email) return false;

    email = email.trim().toLowerCase();

    // 1️⃣ Basic format check (RFC-uyumlu, ama katı değil)
    const emailRegex =
        /^[a-z0-9](?:[a-z0-9._%+-]{0,62}[a-z0-9])?@[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?(?:\.[a-z]{2,})+$/i;

    if (!emailRegex.test(email)) return false;

    // 2️⃣ Ardışık nokta / nokta ile başlama-bitme
    if (email.includes('..')) return false;
    if (email.startsWith('.') || email.endsWith('.')) return false;

    // 3️⃣ Yaygın fake / test / placeholder domainler
    const blockedDomains = [
        'test.com',
        'test.test',
        'tester.com',
        'testing.com',
        'example.com',
        'example.org',
        'example.net',
        'email.com',
        'mail.com',
        'fake.com',
        'fake.net',
        'invalid.com',
        'localhost',
        'a.com',
        'aa.com',
        'aaa.com',
        'abc.com',
        'asdf.com',
        'asdf.asdf',
        'qwerty.com',
        'sample.com',
        'domain.com',
        'noemail.com',
        'nomail.com',
        'none.com'
    ];

    const domain = email.split('@')[1];
    if (blockedDomains.includes(domain)) return false;

    // 4️⃣ Disposable / temp mail provider’ları (en yaygınlar)
    const disposableDomains = [
        'mailinator.com',
        'tempmail.com',
        '10minutemail.com',
        'guerrillamail.com',
        'yopmail.com',
        'trashmail.com',
        'dispostable.com',
        'getnada.com',
        'temp-mail.org'
    ];

    if (disposableDomains.includes(domain)) return false;

    const typoDomains = [
        // gmail
        'gmail.con', 'gmail.co', 'gmail.cm', 'gmail.om', 'gmail.comm', 'gmail.cmo',
        'gmial.com', 'gmai.com', 'gmaill.com', 'gmal.com', 'gmali.com', 'gamil.com',
        'gnail.com', 'gmail.ocm', 'gmail.con.com',
        // yahoo
        'yahoo.con', 'yahoo.co', 'yahoo.cm', 'yaho.com', 'yhaoo.com', 'yahooo.com',
        'yaoo.com', 'yahoo.om', 'yahoo.comm',
        // hotmail
        'hotmail.con', 'hotmail.co', 'hotmail.cm', 'hotmial.com', 'hotmai.com',
        'hotmil.com', 'hotmal.com', 'hotmaill.com', 'hatmail.com', 'hotnail.com',
        // outlook
        'outlook.con', 'outlook.co', 'outlook.cm', 'outlok.com', 'outloo.com',
        'outook.com', 'outlook.comm',
        // icloud
        'icloud.con', 'icloud.co', 'iclod.com', 'iclould.com', 'icould.com', 'iclooud.com',
        // aol
        'aol.con', 'aol.co', 'ao.com',
        // generic TLD typos
        'gmail.xom', 'gmail.vom', 'hotmail.xom'
    ];

    if (typoDomains.includes(domain)) return false;

    // 5️⃣ Aşırı kısa veya saçma local-part
    const localPart = email.split('@')[0];
    if (localPart.length < 2) return false;
    if (/^[0-9]+$/.test(localPart)) return false;

    return true;
}

// Main Formula Settings Class
class FormulaSettings {
    constructor() {
        this.initializeElements();
        this.attachEventListeners();
        this.cacheService = new CacheService();
        this.testMode = CONFIG.TEST_MODE;
        this.detected_upcs = [];
        this.territoryAbortController = null;
        this.brandsAbortController = null;
        this.typesAbortController = null;
        this.settingsAbortController = null;

        // Warranty form variables
        this.firstName = "";
        this.lastName = "";
        this.email = "";
        this.submissionId = null;
        this.clientId = 50;

        this.loadI18n();

        // Hide sections on page load
        this.hideInitialSections();

        // Initialize warranty form handlers
        this.initializeWarrantyForm();

        // Setup scan triggers with a slight delay to ensure DOM is ready
        setTimeout(() => this.setupScanTriggers(), 300);
    }

    loadI18n() {
        const el = document.getElementById('translations-formula-form');
        this.i18n = {};
        this.config = {};
        if (!el) return;
        try { this.i18n = JSON.parse(el.dataset.i18n || '{}'); } catch (e) { utils.error('i18n parse failed', e); }
        try { this.config = JSON.parse(el.dataset.config || '{}'); } catch (e) { utils.error('config parse failed', e); }
    }

    t(key, fallback) {
        return (this.i18n && this.i18n[key]) || fallback;
    }

    initializeElements() {
        // Main containers
        this.upcResults = document.getElementById('upc_results');
        this.upcSettings = document.getElementById('upc_settings');
        this.findStageSetting = document.getElementById('find_stage_setting');
        this.formulaReferenceImages = document.getElementById('formula-reference-images');

        // Form elements
        this.registerFormEmail = document.querySelector('.email-container input');
        this.websiteInput = document.getElementById('website');
        this.userInfo = document.getElementById('userInfo');
        this.warrantyApiUrl = 'https://portal.babybrezza.com/api/warranty/activate';
        this.klaviyoApiUrl = 'https://optin.babybrezza.com/api/subscribe/klaviyo';
        this.reviewApiUrl = 'https://portal.babybrezza.com/api/review';

        this.upcInput = document.getElementById('upc_input');
        this.emailInput = document.getElementById('enter_email_input');
        this.territorySelect = document.getElementById('sel_territory');
        this.brandSelect = document.getElementById('sel_brand');
        this.typeSelect = document.getElementById('sel_type');

        // Hidden fields
        this.territoryId = document.getElementById('sel_territory_id');
        this.brandId = document.getElementById('sel_brand_id');
        this.typeId = document.getElementById('sel_type_id');

        // Product elements
        this.productElements = document.querySelectorAll('.formula-settings-banner__product');
        this.modelTypeInput = document.getElementById('model_type');
        this.productSelectionDiv = document.getElementById('productSelectionDiv');
        this.warrantyRegiDiv = document.getElementById('warranty_regi_div');
        this.formulaProFilter = document.getElementById('formula_pro_filter');

        // Scanner related elements
        this.interactiveElement = document.getElementById('interactive');
        this.upcEnterButton = document.getElementById('upc_enter');

        // Try to find the barcode scanner button in multiple ways
        this.startUpcButton = document.getElementById('start_upc');
        if (!this.startUpcButton) {
            this.startUpcButton = document.getElementById('upc_start');
        }

        // Warranty form elements
        this.registerProductForm = document.querySelector('.register-product-form');
        this.reviewForm = document.querySelector('.submit-review-form');
        this.noThanksLinks = document.querySelectorAll('.noThanks');

        // Make sure we're getting the correct register-product and submit-review elements
        // Try different selector approaches to ensure we find them
        this.registerProduct = document.querySelector('.register-product');
        if (!this.registerProduct) {
            this.registerProduct = document.querySelector('#warranty_regi_div .register-product');
        }

        this.submitReview = document.querySelector('.submit-review');
        if (!this.submitReview) {
            this.submitReview = document.querySelector('#warranty_regi_div .submit-review');
        }
    }

    attachEventListeners() {
        //Checkbox Klaviyo
        const checkbox = document.getElementById('klaviyo-optin');
        if (checkbox) {
            checkbox.addEventListener('change', () => {
                this.selectMarketing(this.websiteInput);
            });
        }

        // UPC input handling
        this.upcInput?.addEventListener('keypress', (e) => {
            if (e.key === 'Enter') {
                this.handleUPCSubmit();
            }
        });

        if (this.upcInput && this.upcEnterButton) {
            this.upcEnterButton.addEventListener('click', () => {
                this.handleUPCSubmit();
            });
        }

        // Email input validation on input change
        this.emailInput?.addEventListener('input', () => {
            // Validate email as user types and remove error if valid
            this.validateEmail();
        });

        // Barcode scanner buttons are now handled in setupScanTriggers method

        // Territory selection
        this.territorySelect?.addEventListener('change', (e) => {
            if (!this.validateEmail()) {
                this.showError(this.t('error_invalid_email', 'Please enter a valid email address'));
                this.territorySelect.selectedIndex = 0;
                this.emailInput.focus();
                return;
            }
            this.handleTerritoryChange(e.target.value);
        });

        // Brand selection
        this.brandSelect?.addEventListener('change', (e) => {
            this.handleBrandChange(e.target.value);
        });

        // Type selection
        this.typeSelect?.addEventListener('change', (e) => {
            this.handleTypeChange(e.target.value);
        });

        // Add product selection handler
        this.productElements.forEach(product => {
            product.addEventListener('click', () => this.handleProductSelection(product));
            product.addEventListener('keypress', (e) => {
                if (e.key === 'Enter') {
                    this.handleProductSelection(product);
                }
            });
        });
    }
    selectMarketing(websiteOrigin, customCheckBoxID = 'klaviyo-optin', emailInputEl = 'enter_email_input'){
        this.addInformationToKlaviyo(customCheckBoxID, emailInputEl);
    }

    validateFormulaUpdatesOptin() {
        const el = document.getElementById('formula-updates-optin');
        if (!el) return true;
        if (el.checked) return true;
        utils.showToast(this.t('error_formula_updates_required', 'Please confirm you want to receive formula updates to view results.'), 'warning');
        return false;
    }
    initBarcodeScanner() {
        if (!this.validateEmail()) {
            this.showError(this.t('error_invalid_email', 'Please enter a valid email address'));
            return;
        }
        if (!this.validateFormulaUpdatesOptin()) {
            return;
        }
        this.selectMarketing(this.websiteInput)

        const resultElement = document.getElementById('result');
        if (resultElement) resultElement.innerHTML = "";

        navigator.mediaDevices.enumerateDevices()
            .then(devices => {
                const hasCamera = devices.some(device => device.kind === 'videoinput');

                if (hasCamera) {
                    Quagga.init({
                        inputStream: {
                            name: "Live",
                            type: "LiveStream",
                            target: this.interactiveElement,
                            constraints: {
                                facingMode: "environment" // or "user" for front camera
                            },
                        },
                        decoder: {
                            readers: ["upc_reader", "ean_reader"], // or other supported barcode types
                        },
                        locator: {
                            patchSize: 5,
                            halfSample: false,
                        },
                        numOfWorkers: navigator.hardwareConcurrency || 1,
                        frequency: 20,
                        locate: true,
                    }, err => {
                        if (err) {
                            console.error('Quagga initialization error:', err);
                            this.showError(this.t('error_scanner_start', 'Error starting the barcode scanner') + ': ' + err.message);
                            return;
                        }

                        // console.log("Quagga initialization finished. Ready to start");
                        Quagga.start();
                        if (this.interactiveElement) {
                            this.interactiveElement.style.display = 'block';
                            setTimeout(() => {
                                scrollToElement(".scanner-wrapper", false, 120)
                            },1000)
                        }

                        // Setup Quagga event handlers
                        this.setupQuaggaHandlers();
                    });
                } else {
                    if (resultElement) {
                        resultElement.innerHTML = '<p style="font-size: 16px; font-weight: 700; text-transform: uppercase; margin:0; color: red;">' + this.t('no_camera_detected', 'No Camera Detected') + '</p>';
                    } else {
                        this.showError(this.t('error_no_camera', 'No camera detected on your device'));
                    }
                }
            })
            .catch(err => {
                console.error("Error accessing media devices:", err);
                this.showError(this.t('error_camera_access', 'Error accessing camera') + ': ' + err.message);
            });
    }

    setupQuaggaHandlers() {
        // Handle detected barcode
        Quagga.onDetected(result => {
            if (this.detected_upcs.includes(result.codeResult.code)) {
                this.detected_upcs = [];
                // Handle the detected barcode
                if (this.upcInput) {
                    this.upcInput.value = result.codeResult.code;
                }
                Quagga.stop();
                if (this.interactiveElement) {
                    this.interactiveElement.style.display = 'none';
                }
                this.handleUPCSubmit();
            } else {
                this.detected_upcs.push(result.codeResult.code);
            }
        });

        // Process barcode scanning results
        Quagga.onProcessed(result => {
            if (!result) return;

            const drawingCtx = Quagga.canvas.ctx.overlay;
            const drawingCanvas = Quagga.canvas.dom.overlay;

            if (drawingCtx && drawingCanvas) {
                drawingCtx.clearRect(0, 0, parseInt(drawingCanvas.getAttribute("width")), parseInt(drawingCanvas.getAttribute("height")));

                if (result.box) {
                    Quagga.ImageDebug.drawPath(result.box, { x: 0, y: 1 }, drawingCtx, { color: "#00F", lineWidth: 2 });
                }

                if (result.codeResult && result.codeResult.code) {
                    // console.log("Barcode detected: " + result.codeResult.code);
                }
            }
        });
    }

    async handleUPCSubmit() {
        if (!this.validateEmail()) {
            this.showError(this.t('error_invalid_email', 'Please enter a valid email address'));
            return;
        }
        if (!this.validateFormulaUpdatesOptin()) {
            return;
        }
        if (!this.validateLotNumber()) {
            return;
        }
        this.selectMarketing(this.websiteInput);

        const upc = this.upcInput.value.trim().replace(/[^a-z0-9]/gi, '');
        if (!upc) {
            this.showError(this.t('error_enter_upc', 'Please enter a UPC code'));
            return;
        }

        const lotInput = document.getElementById('lot_number_input');
        const lotPrefix = lotInput?.getLotPrefix?.() || '';
        const altMfgSetting = this.isFormulaProAdvancedActive() ? (lotPrefix.startsWith('11') ? 'true' : 'false') : '';

        try {
            const response = await this.fetchUPCData(upc, altMfgSetting);
            this.displayUPCResults(response);

            this.sendLegacySettingsLog({
                rawTerritory: response?.territory || '',
                rawBrand:     response?.brand     || '',
                rawType:      response?.type      || ''
            });
        } catch (error) {
            this.showError(this.t('error_fetch_upc', 'Error fetching UPC data'));
            utils.error('UPC Error:', error);
        }
    }

    async fetchUPCData(upc, altMfgSetting = '') {
        const params = new URLSearchParams({ upc });
        if (altMfgSetting) params.set('alt_mfg_setting', altMfgSetting);

        const response = await fetch(`${CONFIG.API_URL}/settings/upc?${params}`);

        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }

        return await response.json();
    }

    // Territory the store prefers to surface first (e.g. "France" on bibybrezza.com)
    getPreferredTerritory() {
        const raw = this.config?.preferred_territory;
        return typeof raw === 'string' ? raw.trim() : '';
    }

    isPreferredTerritory(territory) {
        const preferred = this.getPreferredTerritory().toLowerCase();
        if (!preferred) return false;
        const list = Array.isArray(territory) ? territory : [territory];
        return list.some(t => typeof t === 'string' && t.trim().toLowerCase() === preferred);
    }

    // Stable sort: preferred-territory entries first, original order otherwise
    sortPreferredTerritoryFirst(items, getTerritory) {
        return items
            .map((item, index) => ({ item, index, preferred: this.isPreferredTerritory(getTerritory(item)) }))
            .sort((a, b) => (Number(b.preferred) - Number(a.preferred)) || (a.index - b.index))
            .map(entry => entry.item);
    }

    setPreferredResultHighlight(isPreferred) {
        const mainContent = document.querySelector('.formula-settings-finder__results-content:not(.formula-settings-finder__results-content--extra)');
        if (mainContent) {
            mainContent.classList.toggle('formula-settings-finder__results-content--preferred', !!isPreferred);
        }
    }

    displayUPCResults(data) {
        const items = Array.isArray(data) ? data : (data ? [data] : []);

        const isItemFound = (item) => {
            const hasSetting = item && item.setting !== undefined && item.setting !== null && item.setting !== '';
            const arr = Array.isArray(item?.upc) ? item.upc : [];
            return hasSetting || arr.length > 0;
        };
        const foundItems = this.sortPreferredTerritoryFirst(items.filter(isItemFound), item => item?.territory);

        const resultsSection = document.querySelector('.formula-settings-finder__results');

        if (resultsSection) {
            resultsSection
                .querySelectorAll('.formula-settings-finder__results-content--extra')
                .forEach(el => el.remove());
        }
        this.setPreferredResultHighlight(false);

        if (foundItems.length === 0) {
            const fallbackCode = (items[0]?.upc?.[0]) || (this.upcInput ? this.upcInput.value : '');
            this.upcSettings.innerHTML = `
                <h2 style="text-transform: none; margin-top: 20px; margin-bottom: 15px; font-size: 20px;">
                    <strong>${this.t('results_no_match', 'No Results Found For Barcode:')}</strong> ${fallbackCode}<br>
                    ${this.t('results_select_formula_above', 'Please select formula brand/type above')}
                </h2>`;
            this.findStageSetting.innerHTML = '';

            if (this.formulaReferenceImages) {
                this.formulaReferenceImages.innerHTML = '';
            }
            if (resultsSection) {
                resultsSection.style.display = 'block';
                resultsSection.setAttribute('aria-hidden', 'false');
            }
            if (this.upcResults) {
                this.upcResults.style.display = 'block';
                this.upcResults.setAttribute('aria-hidden', 'false');
            }
            const ctaSectionNoMatch = document.querySelector('.formula-settings-finder__cta');
            if (ctaSectionNoMatch) {
                ctaSectionNoMatch.style.display = 'block';
                ctaSectionNoMatch.setAttribute('aria-hidden', 'false');
            }
            utils.scrollToElement('.formula-settings-finder__results', -105);
            return;
        }

        this.renderUPCItem(foundItems[0], {
            upcResults: this.upcResults,
            upcSettings: this.upcSettings,
            findStageSetting: this.findStageSetting,
            formulaReferenceImages: this.formulaReferenceImages
        });
        this.setPreferredResultHighlight(this.isPreferredTerritory(foundItems[0]?.territory));

        if (resultsSection && foundItems.length > 1) {
            for (let i = 1; i < foundItems.length; i++) {
                const node = this.buildExtraResultsContent(foundItems[i]);
                if (node) resultsSection.appendChild(node);
            }
        }

        if (resultsSection) {
            resultsSection.style.display = 'block';
            resultsSection.setAttribute('aria-hidden', 'false');
        }

        const ctaSection = document.querySelector('.formula-settings-finder__cta');
        if (ctaSection) {
            ctaSection.style.display = 'block';
            ctaSection.setAttribute('aria-hidden', 'false');
        }

        utils.scrollToElement('.formula-settings-finder__results', -105);
    }

    renderUPCItem(item, nodes) {
        const upcArray = Array.isArray(item?.upc) ? item.upc : [];
        const upcCode  = upcArray[0] || (this.upcInput ? this.upcInput.value : '');

        const brand     = item?.brand || '';
        const type      = item?.type  || '';
        const territory = Array.isArray(item?.territory)
            ? item.territory.filter(Boolean).join(', ')
            : (item?.territory || '');

        const isPreferred = this.isPreferredTerritory(item?.territory);

        const metaRows = [
            brand     ? `<p style="color:#000;margin:4px 0;"><strong style="color:#000;">${this.t('results_brand_label', 'Brand')}:</strong> ${brand}</p>`         : '',
            type      ? `<p style="color:#000;margin:4px 0;"><strong style="color:#000;">${this.t('results_type_label', 'Type')}:</strong> ${type}</p>`           : '',
            territory ? `<p class="upc-result-territory" style="color:#000;margin:4px 0;"><strong style="color:#000;">${this.t('results_territory_label', 'Territory')}:</strong> ${territory}</p>` : ''
        ].filter(Boolean).join('');

        // Badge shows the territory name itself (e.g. "France")
        const preferredBadge = isPreferred
            ? `<span class="upc-result-preferred-badge">${this.getPreferredTerritory() || territory}</span>`
            : '';

        if (nodes.upcSettings) {
            nodes.upcSettings.innerHTML = `
                ${preferredBadge}
                <h2 style="text-transform: none; margin-top: ${isPreferred ? '8px' : '20px'}; margin-bottom: 15px; font-size: 20px; color:#000; text-align:left;">
                    <strong>${this.t('results_for_barcode', 'Results for Barcode:')}</strong> ${upcCode}
                </h2>
                ${metaRows ? `<div class="upc-result-meta" style="color:#000;">${metaRows}</div>` : ''}`;
        }
        if (nodes.upcResults) {
            nodes.upcResults.style.display = 'block';
            nodes.upcResults.setAttribute('aria-hidden', 'false');
        }
        if (nodes.findStageSetting) {
            nodes.findStageSetting.innerHTML = this.generateFormulaHTML(item);
        }

        const upcImages = item?.image || item?.images;
        if (nodes.formulaReferenceImages) {
            if (upcImages && upcImages.length > 0) {
                nodes.formulaReferenceImages.innerHTML = this.generateImagesHTML(upcImages);

                if (nodes.findStageSetting) {
                    nodes.findStageSetting.querySelectorAll(
                        '.formula-image-wrapper, .formula-images-container, .settings-note-wrapper'
                    ).forEach(el => el.remove());
                }
            } else {
                nodes.formulaReferenceImages.innerHTML = '<p>' + this.t('no_reference_images', 'No reference images available for this formula.') + '</p>';
            }
        }
    }

    buildExtraResultsContent(item) {
        const original = document.querySelector('.formula-settings-finder__results-content');
        if (!original) return null;

        const clone = original.cloneNode(true);
        clone.classList.add('formula-settings-finder__results-content--extra');
        clone.classList.toggle('formula-settings-finder__results-content--preferred', this.isPreferredTerritory(item?.territory));

        const nodes = {
            upcResults:             clone.querySelector('#upc_results'),
            upcSettings:            clone.querySelector('#upc_settings'),
            findStageSetting:       clone.querySelector('#find_stage_setting'),
            formulaReferenceImages: clone.querySelector('#formula-reference-images')
        };

        Object.values(nodes).forEach(el => el && el.removeAttribute('id'));

        this.renderUPCItem(item, nodes);

        return clone;
    }

    // Clear UPC results when switching to manual dropdown flow
    clearUPCResults() {
        try {
            if (this.upcSettings) {
                this.upcSettings.innerHTML = '';
            }
            if (this.upcResults) {
                this.upcResults.style.display = 'none';
                this.upcResults.setAttribute('aria-hidden', 'true');
            }
            if (this.findStageSetting) {
                this.findStageSetting.innerHTML = '';
            }
            if (this.formulaReferenceImages) {
                this.formulaReferenceImages.innerHTML = '';
            }
            document
                .querySelectorAll('.formula-settings-finder__results-content--extra')
                .forEach(el => el.remove());
            this.setPreferredResultHighlight(false);
        } catch (e) {
            // no-op
        }
    }

    generateFormulaHTML(data) {
        const settingVal = (data?.setting === undefined || data?.setting === null || data?.setting === '')
            ? '-'
            : data.setting;
        const stageVal = (data?.stage === undefined || data?.stage === null || data?.stage === '')
            ? '-'
            : data.stage;
        return `
            <div class="upc-result-wrapper">
                <table>
                    <tr>
                        <th>${this.t('results_stage_header', 'STAGE')}</th>
                        <th>${this.t('results_setting_header', 'SETTING')}</th>
                    </tr>
                    <tr>
                        <td class="${stageVal === '-' ? 'error' : ''}">${stageVal}</td>
                        <td class="${settingVal === '-' ? 'error' : ''}">${settingVal}</td>
                    </tr>
                </table>
            </div>`;
    }

    generateImagesHTML(images) {
        if (!images || !images.length) return '';

        return images.map(image => `
            <div class="formula-image-wrapper">
                <img class="formula-image"
                    data-image-preview
                    style="display: block; margin-left: auto; margin-right: auto; max-width: 250px; max-height: 300px;"
                    alt="${image}"
                    src="${CONFIG.IMAGE_BASE}/${image}"
                    loading="lazy"/>
            </div>
        `).join('');
    }
    async addInformationToKlaviyo(checkBoxId = 'klaviyo-optin',emailInputData = 'enter_email_input') {
        const isWarranty = checkBoxId === 'warranty-optin';
        const klaviyoEnabled = isWarranty
            ? this.config?.klaviyo_warranty_enabled
            : this.config?.klaviyo_filter_enabled;
        if (!klaviyoEnabled) {
            return;
        }
        const optInEl = document.getElementById(checkBoxId);
        if (!optInEl || !optInEl.checked) {
            return;
        }
        const emailInputEl = document.getElementById(emailInputData);
        const email = emailInputEl ? emailInputEl.value : '';
        if (!email) {
            return;
        }
        const activeProductEl = document.querySelector('.formula-settings-banner__product.active');
        const selectedTitleEl = document.getElementById('selected-product-title');
        const selectedProduct = (selectedTitleEl && selectedTitleEl.textContent.trim())
            || (activeProductEl && activeProductEl.getAttribute('data-product-name'))
            || '';
        const store = (this.config && this.config.klaviyo_store)
            || (this.websiteInput && this.websiteInput.value)
            || '';

        try {
            const response = await fetch("https://optin.babybrezza.com/api/subscribe/klaviyo", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "revision": "2023-10-15"
                },
                body: JSON.stringify({
                    email: email,
                    store: store,
                    first_name: "Default",
                    last_name: "User",
                    selectedProduct: selectedProduct
                })
            });

            if (!response.ok) {
                const error = await response.text();
                console.log(error)
                return
            }
        } catch (err) {
            console.log(err)
        }
    }

    updateMissingLink(country){
        const safeDecode = (v) => {
            if (!v) return '';
            try { return decodeURIComponent(v); } catch (e) { return v; }
        };

        const activeProductEl = document.querySelector('.formula-settings-banner__product.active');
        const productName = activeProductEl ? activeProductEl.getAttribute("data-product-name") : '';

        const params = new URLSearchParams();
        if (productName) params.append('product', productName);
        if (country) params.append('country', safeDecode(country));

        const stage = this.typeSelect ? this.typeSelect.value : '';
        if (stage) params.append('formula_name', safeDecode(stage));

        const kbBase = (this.config && this.config.help_knowledge_base_url) || 'https://knowledge.babybrezza.com/s/formula-settings';
        const link = `${kbBase}?${params.toString()}`;
        const brandLinkEl = document.getElementById("missing_brand_brand");
        if (brandLinkEl) brandLinkEl.setAttribute("href", link);
        const typeLinkEl = document.getElementById("missing_brand_type");
        if (typeLinkEl) typeLinkEl.setAttribute("href", link);
    }

    async handleTerritoryChange(territory) {
        // Clear leftover UPC result when switching to manual selection
        this.clearUPCResults();
        if (!this.validateEmail()) {
            this.territorySelect.selectedIndex = 0;
            this.emailInput.focus();
            return;
        }
        if (!this.validateLotNumber()) {
            this.territorySelect.selectedIndex = 0;
            return;
        }

        if (!territory) {
            console.error('No territory selected');
            return;
        }

        // Format territory for URL encoding
        let formattedTerritory = territory;
        { const el = document.getElementById("choose_brand_link"); if (el) el.style.display = "inline-block"; }
        this.updateMissingLink(territory);
        // Set hidden field
        if (this.territoryId) {
            this.territoryId.value = formattedTerritory;
        } else {
            console.error('Territory ID field not found');
        }

        // Reset brand and type selects
        if (this.brandSelect) {
            this.brandSelect.innerHTML = '<option value="">' + this.t('default_option_brand', '-- Choose Your Formula Brand --') + '</option>';
            this.enhanceSelectSearch(this.brandSelect);
        }

        if (this.typeSelect) {
            this.typeSelect.innerHTML = '<option value="">' + this.t('default_option_type', '-- Choose Your Formula Type --') + '</option>';
            this.enhanceSelectSearch(this.typeSelect);
        }

        if (this.findStageSetting) {
            this.findStageSetting.innerHTML = '';
        }

        this.setSelectLoading(this.brandSelect, true);
        try {
            const response = await this.fetchBrands(formattedTerritory);
            this.updateBrandSelect(response);
        } catch (error) {
            if (error.name !== 'AbortError') {
                this.showError(this.t('error_fetch_brands', 'Error fetching brands'));
                utils.error('Brand Error:', error);
            }
        } finally {
            this.setSelectLoading(this.brandSelect, false);
        }
    }

    async fetchBrands(territory) {
        if (this.brandsAbortController) {
            this.brandsAbortController.abort();
        }
        this.brandsAbortController = new AbortController();
        const signal = this.brandsAbortController.signal;

        let rawTerritory = territory || '';
        try { rawTerritory = decodeURIComponent(rawTerritory); } catch (_) {}
        const cacheKey = `brands_${rawTerritory}`;

        return await this.cacheService.get(cacheKey, async () => {
            const params = new URLSearchParams({ territory: rawTerritory });
            const response = await fetch(`${CONFIG.API_URL}/brands?${params}`, { signal });

            if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

            const data = await response.json();
            // data is a plain string array: ["Hipp", "Aptamil", ...]
            return { brands: data, defaultLabel: this.t('default_option_brand', '-- Choose Your Formula Brand --') };
        });
    }

    updateBrandSelect(data) {
        if (!this.brandSelect) {
            return;
        }

        let html = `<option value="">${data.defaultLabel || 'Choose Your Formula Brand'}</option>`;

        if (Array.isArray(data.brands)) {
            data.brands.forEach(brand => {
                // Use the raw brand name as value and display text
                html += `<option value="${brand}">${brand}</option>`;
            });

            this.brandSelect.innerHTML = html;
            this.enhanceSelectSearch(this.brandSelect);
        } else {
            this.showError(this.t('error_invalid_brand_data', 'Error: Invalid brand data received from server'));
        }
    }

    validateLotNumber() {
        const wrapper = document.querySelector('.lot-number-wrapper');
        // Only required when the lot field is visible (Formula Pro Advanced)
        if (!wrapper || wrapper.style.display === 'none') return true;

        const lotInput = document.getElementById('lot_number_input');
        const typed = lotInput?.getLotPrefix?.() || '';

        if (typed.length < 2) {
            this.showLotError(this.t('lot_required', 'Please enter your lot number'));
            return false;
        }

        // Clear any existing lot error
        document.querySelectorAll('.lot-number-error').forEach(e => e.remove());
        return true;
    }

    showLotError(message) {
        document.querySelectorAll('.lot-number-error').forEach(e => e.remove());

        const errorDiv = document.createElement('div');
        errorDiv.className = 'lot-number-error';
        errorDiv.style.color = 'red';
        errorDiv.style.padding = '1rem';
        errorDiv.style.marginTop = '1rem';
        errorDiv.style.border = '1px solid red';
        errorDiv.style.borderRadius = '0.25rem';
        errorDiv.style.backgroundColor = 'rgba(255, 0, 0, 0.1)';
        errorDiv.textContent = message;

        const wrapper = document.querySelector('.lot-number-wrapper');
        if (wrapper) wrapper.appendChild(errorDiv);

        utils.scrollToElement('#lot_number_input', -150);

        setTimeout(() => {
            const lotInput = document.getElementById('lot_number_input');
            if (lotInput) lotInput.focus();
        }, 400);
    }

    validateEmail() {
        if (!this.emailInput) return true;

        const email = this.emailInput.value;

        if (validEmail(email)) {
            this.emailInput.classList.remove('invalid');
            this.emailInput.classList.add('email-valid');
            // Remove any existing error messages when email is valid
            const existingErrors = document.querySelectorAll('.formula-settings-error');
            existingErrors.forEach(err => err.remove());
            return true;
        } else {
            this.emailInput.classList.add('invalid');
            this.emailInput.classList.remove('email-valid');
            return false;
        }
    }

    setSelectLoading(selectEl, isLoading) {
        if (!selectEl) return;
        const wrapper = selectEl.closest('.filter-select-wrapper');
        if (!wrapper) return;
        wrapper.classList.toggle('loading', isLoading);
        selectEl.disabled = isLoading;
        const searchInput = wrapper.querySelector('.search-select-input');
        if (searchInput) searchInput.disabled = isLoading;
    }

    enhanceSelectSearch(selectEl) {
        if (!selectEl || !selectEl.parentNode) return;
        let searchInput = selectEl.parentNode.querySelector(':scope > .search-select-input');
        let dropdown = selectEl.parentNode.querySelector(':scope > .search-select-dropdown');

        if (!searchInput) {
            selectEl.style.display = 'none';
            selectEl.setAttribute('tabindex', '-1');
            selectEl.setAttribute('aria-hidden', 'true');

            searchInput = document.createElement('input');
            searchInput.type = 'search';
            searchInput.className = 'search-select-input';
            searchInput.name = 'search-' + Math.random().toString(36).slice(2, 10);
            searchInput.setAttribute('autocomplete', 'off');
            searchInput.setAttribute('autocorrect', 'off');
            searchInput.setAttribute('autocapitalize', 'off');
            searchInput.setAttribute('spellcheck', 'false');
            searchInput.setAttribute('data-form-type', 'other');
            searchInput.setAttribute('data-lpignore', 'true');
            searchInput.setAttribute('data-1p-ignore', '');
            searchInput.setAttribute('role', 'combobox');
            selectEl.insertAdjacentElement('afterend', searchInput);

            dropdown = document.createElement('div');
            dropdown.className = 'search-select-dropdown';
            dropdown.hidden = true;
            dropdown.setAttribute('role', 'listbox');
            document.body.appendChild(dropdown);

            const positionDropdown = () => {
                const rect = searchInput.getBoundingClientRect();
                const vv = window.visualViewport;
                const vvTop = vv ? vv.offsetTop : 0;
                const vvLeft = vv ? vv.offsetLeft : 0;
                const vvHeight = vv ? vv.height : window.innerHeight;
                const spaceBelow = vvHeight - rect.bottom;
                const availableH = Math.max(120, Math.min(260, spaceBelow - 8));
                dropdown.style.maxHeight = availableH + 'px';
                dropdown.style.left = (rect.left + vvLeft) + 'px';
                dropdown.style.width = rect.width + 'px';
                dropdown.style.top = (rect.bottom + vvTop + 2) + 'px';
            };
            searchInput._positionDropdown = positionDropdown;

            window.addEventListener('scroll', () => { if (!dropdown.hidden) positionDropdown(); }, true);
            window.addEventListener('resize', () => { if (!dropdown.hidden) positionDropdown(); });
            if (window.visualViewport) {
                window.visualViewport.addEventListener('resize', () => { if (!dropdown.hidden) positionDropdown(); });
                window.visualViewport.addEventListener('scroll', () => { if (!dropdown.hidden) positionDropdown(); });
            }

            const renderOptions = (filter = '') => {
                dropdown.innerHTML = '';
                const f = filter.trim().toLowerCase();
                const items = Array.from(selectEl.options).filter(o => o.value);
                const matches = f ? items.filter(o => o.text.toLowerCase().includes(f)) : items;

                if (matches.length === 0) {
                    const empty = document.createElement('div');
                    empty.className = 'search-select-empty';
                    empty.textContent = this.t('search_no_matches', 'No matches');
                    dropdown.appendChild(empty);
                    return;
                }

                matches.forEach(opt => {
                    const item = document.createElement('div');
                    item.className = 'search-select-option';
                    item.textContent = opt.text;
                    item.dataset.value = opt.value;
                    item.setAttribute('role', 'option');
                    if (opt.value === selectEl.value) item.classList.add('is-active');
                    item.addEventListener('mousedown', (e) => {
                        e.preventDefault();
                        selectEl.value = opt.value;
                        searchInput.value = opt.text;
                        dropdown.hidden = true;
                        selectEl.dispatchEvent(new Event('change', { bubbles: true }));
                    });
                    dropdown.appendChild(item);
                });
            };

            const guardSearchInput = () => {
                if (selectEl.id === 'sel_brand') {
                    const territoryId = document.getElementById('sel_territory_id');
                    if (!territoryId || !territoryId.value) {
                        utils.showToast(this.t('toast_select_country_first', 'Please select your country first.'));
                        return false;
                    }
                }
                if (selectEl.id === 'sel_type') {
                    const brandId = document.getElementById('sel_brand_id');
                    if (!brandId || !brandId.value) {
                        utils.showToast(this.t('toast_select_brand_first', 'Please select your formula brand first.'));
                        return false;
                    }
                }
                return true;
            };

            searchInput.addEventListener('pointerdown', (e) => {
                if (!guardSearchInput()) {
                    e.preventDefault();
                    searchInput.blur();
                }
            });
            searchInput.addEventListener('focus', () => {
                if (!guardSearchInput()) {
                    searchInput.blur();
                    return;
                }
                renderOptions('');
                searchInput.select();
                positionDropdown();
                dropdown.hidden = false;
            });
            searchInput.addEventListener('click', () => {
                if (!guardSearchInput()) {
                    searchInput.blur();
                    return;
                }
                if (dropdown.hidden) {
                    renderOptions('');
                    searchInput.select();
                    positionDropdown();
                    dropdown.hidden = false;
                }
            });
            searchInput.addEventListener('input', () => {
                if (!guardSearchInput()) {
                    searchInput.value = '';
                    searchInput.blur();
                    dropdown.hidden = true;
                    return;
                }
                renderOptions(searchInput.value);
                positionDropdown();
                dropdown.hidden = false;
            });
            searchInput.addEventListener('blur', () => {
                setTimeout(() => {
                    dropdown.hidden = true;
                    const selOpt = selectEl.options[selectEl.selectedIndex];
                    searchInput.value = (selOpt && selOpt.value) ? selOpt.text : '';
                }, 150);
            });
            searchInput.addEventListener('keydown', (e) => {
                if (e.key === 'Escape') {
                    dropdown.hidden = true;
                    searchInput.blur();
                }
            });

            searchInput._renderOptions = renderOptions;
        }

        const firstOpt = selectEl.options[0];
        const selOpt = selectEl.options[selectEl.selectedIndex];
        searchInput.placeholder = firstOpt ? firstOpt.text : 'Select...';
        searchInput.value = (selOpt && selOpt.value) ? selOpt.text : '';

        if (searchInput._renderOptions) searchInput._renderOptions('');
    }

    showError(message) {
        utils.error(message);

        // Display error to the user
        const errorDiv = document.createElement('div');
        errorDiv.className = 'formula-settings-error';
        errorDiv.style.color = 'red';
        errorDiv.style.padding = '1rem';
        errorDiv.style.border = '1px solid red';
        errorDiv.style.borderRadius = '0.25rem';
        errorDiv.style.backgroundColor = 'rgba(255, 0, 0, 0.1)';
        errorDiv.textContent = message;

        // Remove any existing error messages
        const existingErrors = document.querySelectorAll('.formula-settings-error');
        existingErrors.forEach(err => err.remove());

        // Add the error message right after the email input
        if (this.emailInput) {
            this.emailInput.insertAdjacentElement('afterend', errorDiv);
        } else if (this.findStageSetting) {
            this.findStageSetting.innerHTML = '';
            this.findStageSetting.appendChild(errorDiv);
        }
    }

    handleProductSelection(product) {
        // No-op when re-clicking the already-active product
        if (product.classList.contains('active')) {
            return;
        }

        if (this.territoryAbortController) this.territoryAbortController.abort();
        if (this.brandsAbortController) this.brandsAbortController.abort();
        if (this.typesAbortController) this.typesAbortController.abort();
        if (this.settingsAbortController) this.settingsAbortController.abort();

        // Hide any previously-shown result/crossell sections immediately on product switch
        [
            '.section--dynamic-listing',
            '.result-disc-section',
            '.formula-settings-finder__cta',
            '.formula-settings-finder__results'
        ].forEach(sel => {
            document.querySelectorAll(sel).forEach(el => {
                el.style.display = 'none';
                el.setAttribute('aria-hidden', 'true');
            });
        });
        ['enjoy-msg', 'fps-mail-cta', 'more-products-wrapper'].forEach(id => {
            const el = document.getElementById(id);
            if (el) el.style.display = 'none';
        });

        // Reset filter inputs and opt-in checkboxes so a product switch matches a fresh-page first click
        if (this.emailInput) {
            this.emailInput.value = '';
            this.emailInput.classList.remove('invalid', 'email-valid');
        }
        if (this.upcInput) this.upcInput.value = '';
        ['formula-updates-optin', 'klaviyo-optin', 'warranty-optin', 'acceptance'].forEach(id => {
            const cb = document.getElementById(id);
            if (cb) cb.checked = false;
        });
        this.clearUPCResults();
        if (this.formulaProFilter) {
            this.formulaProFilter.style.display = 'none';
            this.formulaProFilter.setAttribute('aria-hidden', 'true');
        }

        const modelType = product.dataset.modelType;
        const productId = product.dataset.productId;

        if (!modelType) {
            this.showError(this.t('error_model_type_missing', 'Error: Model type not found. Please ensure products are configured correctly.'));
            return;
        }
        { const el = document.getElementById("choose_brand_link"); if (el) el.style.display = "none"; }
        { const el = document.getElementById("choose_brand_type"); if (el) el.style.display = "none"; }
        // Update selected state
        this.productElements.forEach(p => p.classList.remove('active'));
        product.classList.add('active');

        // Update the selected product title in the filter section
        const selectedProductTitle = document.getElementById('selected-product-title');
        const warrantyProductTitle = document.getElementById('warranty-product-title');
        if (selectedProductTitle) {
            // Get the product title from the selected product element
            const productTitle = product.querySelector('.product-title-wrapper p').textContent.trim();
            selectedProductTitle.textContent = productTitle;

            // Also update the warranty form product title
            if (warrantyProductTitle) {
                warrantyProductTitle.textContent = productTitle;
            }
        }

        // Always use 'advanced' as the model type for server requests
        const serverModelType = 'advanced';

        // Set the model type for the form
        if (this.modelTypeInput) {
            this.modelTypeInput.value = serverModelType;
        } else {
            this.showError(this.t('error_model_type_input_missing', 'Error: Model type input field not found. Please check your page configuration.'));
            return;
        }

        // Update product selection in contact form if it exists
        const contactFormProduct = document.querySelector('#contactFormProduct');
        if (contactFormProduct) {
            const option = contactFormProduct.querySelector(`option[value="${productId}"]`);
            if (option) {
                option.selected = true;
            }
        }

        // Reset all selects and hidden fields
        if (this.territorySelect) {
            this.territorySelect.innerHTML = '<option value="">' + this.t('default_option_territory', '-- Choose Your Territory --') + '</option>';
            this.enhanceSelectSearch(this.territorySelect);
        }

        if (this.brandSelect) {
            this.brandSelect.innerHTML = '<option value="">' + this.t('default_option_brand', '-- Choose Your Formula Brand --') + '</option>';
            this.enhanceSelectSearch(this.brandSelect);
        }

        if (this.typeSelect) {
            this.typeSelect.innerHTML = '<option value="">' + this.t('default_option_type', '-- Choose Your Formula Type --') + '</option>';
            this.enhanceSelectSearch(this.typeSelect);
        }

        if (this.territoryId) this.territoryId.value = '';
        if (this.brandId) this.brandId.value = '';
        if (this.typeId) this.typeId.value = '';

        if (this.findStageSetting) {
            this.findStageSetting.innerHTML = '';
        }

        // Hide product selection
        if (this.productSelectionDiv) {
            this.productSelectionDiv.style.display = 'none';
        }

        const warrantyEnabled = this.warrantyRegiDiv && this.warrantyRegiDiv.dataset.enabled === 'true';

        if (warrantyEnabled) {
            this.warrantyRegiDiv.style.display = 'block';
            this.warrantyRegiDiv.removeAttribute('aria-hidden');

            const register_el = document.querySelector('.register-product');
            const submit_el = document.querySelector('.submit-review');
            if (register_el) {
                register_el.style.removeProperty('display');
                register_el.style.removeProperty('visibility');
            }
            if (submit_el) {
                submit_el.style.setProperty('display', 'none', 'important');
                submit_el.style.setProperty('visibility', 'hidden', 'important');
            }
        } else {
            if (this.warrantyRegiDiv) {
                this.warrantyRegiDiv.style.display = 'none';
                this.warrantyRegiDiv.setAttribute('aria-hidden', 'true');
            }

            const register_el = document.querySelector('.register-product');
            const submit_el = document.querySelector('.submit-review');
            if (register_el) {
                register_el.style.setProperty('display', 'none', 'important');
                register_el.style.setProperty('visibility', 'hidden', 'important');
            }
            if (submit_el) {
                submit_el.style.setProperty('display', 'none', 'important');
                submit_el.style.setProperty('visibility', 'hidden', 'important');
            }

            if (this.formulaProFilter) {
                this.formulaProFilter.style.display = 'block';
                this.formulaProFilter.setAttribute('aria-hidden', 'false');
            }
        }

        // Get territory data for the selected model
        try {
            this.getTerritoryData();
        } catch (error) {
            this.showError(this.t('error_fetch_territories', 'Error fetching territories. Please try again later.'));
        }

        // Scroll to warranty form or formula filter section with offset
        if (this.warrantyRegiDiv && this.warrantyRegiDiv.style.display !== 'none') {
            utils.scrollToElement('#warranty_regi_div', -105); // Scroll 100px above the element
        } else if (this.formulaProFilter) {
            utils.scrollToElement('#formula_pro_filter', -105); // Scroll 100px above the element
        }

        // Ensure CTA and results sections remain hidden
        const ctaSection = document.querySelector('.formula-settings-finder__cta');
        if (ctaSection) {
            ctaSection.style.display = 'none';
        }

        const resultsSection = document.querySelector('.formula-settings-finder__results');
        if (resultsSection) {
            resultsSection.style.display = 'none';
        }
    }

    getTerritoryData() {
        if (this.territoryAbortController) {
            this.territoryAbortController.abort();
        }
        this.territoryAbortController = new AbortController();
        const signal = this.territoryAbortController.signal;

        this.setSelectLoading(this.territorySelect, true);
        fetch(`${CONFIG.API_URL}/territories`, { signal })
        .then(async response => {
            if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
            return response.json();
        })
        .then(data => {
            // data is a plain string array: ["Germany", "United States of America", ...]
            if (!Array.isArray(data)) throw new Error('Invalid territory data format');
            this.updateTerritorySelect({ territories: data, defaultLabel: this.t('default_option_territory', '-- Choose Your Territory --') });
            this.selectMarketing(this.websiteInput);
        })
        .catch(error => {
            if (error.name !== 'AbortError') {
                this.showError('Error fetching territories: ' + error.message);
            }
        })
        .finally(() => {
            this.setSelectLoading(this.territorySelect, false);
        });
    }

    updateTerritorySelect(data) {
        if (!this.territorySelect) {
            return;
        }

        let html = `<option value="">${data.defaultLabel || 'Choose Your Territory'}</option>`;

        if (Array.isArray(data.territories)) {
            // Preferred territory (e.g. France) is listed first, the rest keep API order
            const orderedTerritories = this.sortPreferredTerritoryFirst(data.territories, t => t);
            orderedTerritories.forEach(territory => {
                html += `<option value="${encodeURIComponent(territory)}">${territory}</option>`;
            });

            this.territorySelect.innerHTML = html;
            this.enhanceSelectSearch(this.territorySelect);
        } else {
            this.showError(this.t('error_invalid_territory_data', 'Error: Invalid territory data received from server'));
        }
    }

    async handleBrandChange(brandId) {
        // Clear leftover UPC result when switching to manual selection
        this.clearUPCResults();
        if (!this.validateEmail()) {
            this.brandSelect.selectedIndex = 0;
            this.emailInput.focus();
            return;
        }
        if (!this.validateLotNumber()) {
            this.brandSelect.selectedIndex = 0;
            return;
        }

        const formattedBrandId = brandId || '';

        // Set hidden field
        if (this.brandId) {
            this.brandId.value = formattedBrandId;
        }

        { const el = document.getElementById("choose_brand_type"); if (el) el.style.display = "inline-block"; }
        this.updateMissingLink(this.territorySelect.value);
        // Reset type select
        if (this.typeSelect) {
            this.typeSelect.innerHTML = '<option value="">' + this.t('default_option_type', '-- Choose Your Formula Type --') + '</option>';
            this.enhanceSelectSearch(this.typeSelect);
        }

        if (this.findStageSetting) {
            this.findStageSetting.innerHTML = '';
        }

        this.setSelectLoading(this.typeSelect, true);
        try {
            const response = await this.fetchTypes(formattedBrandId);
            this.updateTypeSelect(response);
            this.selectMarketing(this.websiteInput);
        } catch (error) {
            if (error.name !== 'AbortError') {
                this.showError(this.t('error_fetch_types', 'Error fetching formula types'));
                utils.error('Type Error:', error);
            }
        } finally {
            this.setSelectLoading(this.typeSelect, false);
        }
    }

    async fetchTypes(brandId) {
        if (this.typesAbortController) {
            this.typesAbortController.abort();
        }
        this.typesAbortController = new AbortController();
        const signal = this.typesAbortController.signal;

        const safeDecode = (v) => {
            if (!v) return '';
            try { return decodeURIComponent(v); } catch (_) { return v; }
        };
        const rawTerritory = safeDecode(this.territoryId ? this.territoryId.value : '');
        const rawBrand = safeDecode(brandId || '');
        const cacheKey = `types_${rawTerritory}_${rawBrand}`;

        return await this.cacheService.get(cacheKey, async () => {
            const params = new URLSearchParams({ territory: rawTerritory, brand: rawBrand });
            const response = await fetch(`${CONFIG.API_URL}/types?${params}`, { signal });

            if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

            const data = await response.json();
            // data is a plain string array: ["Combiotik", "Bio Combiotik", ...]
            return { types: data, defaultLabel: this.t('default_option_type', '-- Choose Your Formula Type --') };
        });
    }

    updateTypeSelect(data) {
        if (!this.typeSelect) {
            return;
        }

        let html = `<option value="">${data.defaultLabel || 'Choose Your Formula Type'}</option>`;

        if (Array.isArray(data.types)) {
            data.types.forEach(type => {
                // Use the raw type name as value and display text
                html += `<option value="${type}">${type}</option>`;
            });

            this.typeSelect.innerHTML = html;
            this.enhanceSelectSearch(this.typeSelect);
        } else {
            this.showError(this.t('error_invalid_type_data', 'Error: Invalid type data received from server'));
        }
    }

    async handleTypeChange(typeId) {
        // Clear leftover UPC result when switching to manual selection
        this.clearUPCResults();
        if (!this.validateEmail()) {
            this.typeSelect.selectedIndex = 0;
            this.emailInput.focus();
            return;
        }
        if (!this.validateFormulaUpdatesOptin()) {
            this.typeSelect.selectedIndex = 0;
            return;
        }
        if (!this.validateLotNumber()) {
            this.typeSelect.selectedIndex = 0;
            return;
        }

        // Handle dynamic listing section visibility
        const dynamicListingSection = document.querySelector('.section--dynamic-listing');
        const resultDiscSection = document.querySelector('.result-disc-section');
        if (dynamicListingSection) {
            dynamicListingSection.style.display = 'none'; // Keep hidden initially
        }

        // Set hidden field
        if (this.typeId) {
            this.typeId.value = typeId;
        }

        this.updateMissingLink(this.territorySelect ? this.territorySelect.value : '');

        // Show the results section immediately so the loading spinner is visible
        // while we fetch settings (the section is hidden by default).
        const resultsSection = document.querySelector('.formula-settings-finder__results');
        if (resultsSection) {
            resultsSection.style.display = 'block';
        }

        if (this.findStageSetting) {
            this.findStageSetting.innerHTML = `
                <div class="formula-settings-loading" role="status" aria-live="polite">
                    <span class="formula-settings-loading__spinner" aria-hidden="true"></span>
                    <span class="formula-settings-loading__text">${this.t('loading_settings', 'Loading settings…')}</span>
                </div>`;
        }

        try {
            const response = await this.fetchSettings(typeId);
            this.displaySettings(response);

            // Show CTA section first
            const ctaSection = document.querySelector('.formula-settings-finder__cta');
            if (ctaSection) {
                ctaSection.style.display = 'block';
            }

            // Finally show dynamic listing section after a short delay to ensure proper sequence
            setTimeout(() => {
                this.selectMarketing(this.websiteInput)
                if (dynamicListingSection) {
                    dynamicListingSection.style.display = 'block';
                }
                if (resultDiscSection) {
                    resultDiscSection.style.display = 'block';
                }
            }, 100);

        } catch (error) {
            if (error.name !== 'AbortError') {
                this.showError(this.t('error_fetch_settings', 'Error fetching formula settings'));
                utils.error('Settings Error:', error);
                if (this.findStageSetting) {
                    this.findStageSetting.innerHTML = '';
                }
            }
        }
    }

    isFormulaProAdvancedActive() {
        const activeProductEl = document.querySelector('.formula-settings-banner__product.active');
        if (!activeProductEl) return false;
        const url  = (activeProductEl.dataset?.productUrl  || '').toLowerCase();
        const name = (activeProductEl.dataset?.productName || '').toLowerCase();
        if (url.includes('wifi') || name.includes('wifi')) return true;
        return url.includes('formula-pro-advanced')
            || name.includes('formula pro® advanced')
            || name.includes('formula pro advanced');
    }

    sendLegacySettingsLog({ rawTerritory = '', rawBrand = '', rawType = '' } = {}) {
        try {
            const safeDecode = (v) => {
                if (!v) return '';
                try { return decodeURIComponent(v); } catch (e) { return v; }
            };

            const territory = rawTerritory || safeDecode(this.territoryId ? this.territoryId.value : '');
            const brandRaw  = rawBrand     || safeDecode(this.brandId     ? this.brandId.value     : '');
            const type      = rawType      || safeDecode(this.typeId      ? this.typeId.value      : '');

            const activeProductEl = document.querySelector('.formula-settings-banner__product.active');
            const modelType = activeProductEl ? (activeProductEl.getAttribute('data-type') || '') : '';

            const formData = new URLSearchParams();
            formData.append('type_id', type);
            formData.append('brand_id', brandRaw ? encodeURIComponent(brandRaw) : '');
            formData.append('territory_id', territory);
            formData.append('model_type', modelType);
            formData.append('email', this.emailInput ? this.emailInput.value : '');
            formData.append('language', document.documentElement.lang || 'en');
            formData.append('ip_address', utils.getCookie('ip_address') || '');
            formData.append('user_agent', navigator.userAgent);
            formData.append('touch_device', String('ontouchstart' in window || navigator.maxTouchPoints > 0));
            formData.append('state_prov', utils.getCookie('state_prov') || '');
            formData.append('zip', utils.getCookie('zip') || '');
            formData.append('country', utils.getCookie('country') || '');
            formData.append('city', utils.getCookie('city') || '');
            formData.append('babyb_store', 'bibybrezza.com');

            const lotInputEl = document.getElementById('lot_number_input');
            const lotNumber = lotInputEl ? (lotInputEl.value || '').trim() : '';
            if (lotNumber) {
                formData.append('lot_number', lotNumber);
            }

            fetch('https://babybrezzaserver.com/index.php/without_response/getsetting', {
                method: 'POST',
                body: formData,
                keepalive: true
            }).catch(() => {});
        } catch (e) {}
    }

    async fetchSettings(typeId) {
        if (this.settingsAbortController) {
            this.settingsAbortController.abort();
        }
        this.settingsAbortController = new AbortController();
        const signal = this.settingsAbortController.signal;

        const rawTerritory = decodeURIComponent(this.territoryId ? this.territoryId.value : '');
        const rawBrand     = decodeURIComponent(this.brandId ? this.brandId.value : '');
        const rawType      = decodeURIComponent(typeId || '');

        this.sendLegacySettingsLog({ rawTerritory, rawBrand, rawType });

        const stagesParams = new URLSearchParams({ territory: rawTerritory, brand: rawBrand, type: rawType });
        const stagesResponse = await fetch(`${CONFIG.API_URL}/stages?${stagesParams}`, { signal });
        if (!stagesResponse.ok) throw new Error(`HTTP error! status: ${stagesResponse.status}`);

        const stagesData = await stagesResponse.json();
        if (!Array.isArray(stagesData)) return [];

        const lotInput = document.getElementById('lot_number_input');
        const lotPrefix = lotInput?.getLotPrefix?.() || '';
        const altMfgSetting = this.isFormulaProAdvancedActive() ? (lotPrefix.startsWith('11') ? 'true' : 'false') : '';

        const results = await Promise.all(stagesData.map(async (stageItem) => {
            let stageValue, stageName;
            if (stageItem && typeof stageItem === 'object') {
                stageValue = stageItem.stage ?? stageItem.number ?? '';
                stageName = stageItem.name ?? stageItem.label ?? String(stageValue);
            } else {
                stageValue = String(stageItem);
                stageName = stageValue;
            }

            const settingsParams = new URLSearchParams({
                territory: rawTerritory,
                brand: rawBrand,
                type: rawType
            });
            if (stageValue !== '' && stageValue !== undefined && stageValue !== null) {
                settingsParams.set('stage', stageValue);
            }
            if (altMfgSetting) settingsParams.set('alt_mfg_setting', altMfgSetting);

            const settingsResp = await fetch(`${CONFIG.API_URL}/settings?${settingsParams}`, { signal });
            if (!settingsResp.ok) throw new Error(`HTTP error! status: ${settingsResp.status}`);
            const settingsData = await settingsResp.json();

            return {
                stage: stageName,
                stageNum: stageValue,
                setting: settingsData?.setting,
                upc: settingsData?.upc || [],
                image: settingsData?.image || []
            };
        }));

        return results;
    }

    displaySettings(data) {
        if (!this.findStageSetting) {
            return;
        }

        // data is an array of { stage, stageNum, setting, upc, image } from fetchSettings
        if (Array.isArray(data)) {
            if (data.length === 0) {
                this.findStageSetting.innerHTML = '<p>' + this.t('no_settings_found', 'No settings found for this formula.') + '</p>';
            } else {
                const tableRows = data.map(r => {
                    const settingVal = (r.setting === undefined || r.setting === null || r.setting === '') ? '-' : r.setting;
                    return `<tr><td>${r.stage}</td><td>${settingVal}</td></tr>`;
                }).join('');

                this.findStageSetting.innerHTML = `
                    <div class="formula-settings-result-wrapper">
                        <table>
                            <tr>
                                <th>${this.t('results_stage_header', 'STAGE')}</th>
                                <th>${this.t('results_setting_header', 'SETTING')}</th>
                            </tr>
                            ${tableRows}
                        </table>
                    </div>`;
            }

            if (this.formulaReferenceImages) {
                const seen = new Set();
                const allImages = [];
                data.forEach(r => (r.image || []).forEach(img => {
                    if (!seen.has(img)) { seen.add(img); allImages.push(img); }
                }));

                if (allImages.length > 0) {
                    this.formulaReferenceImages.innerHTML = this.generateImagesHTML(allImages);
                } else {
                    this.formulaReferenceImages.innerHTML = '';
                }
            }
        }

        // Show CTA and results sections
        const ctaSection = document.querySelector('.formula-settings-finder__cta');
        const resultsSection = document.querySelector('.formula-settings-finder__results');

        if (ctaSection) {
            ctaSection.style.display = 'block';
            ctaSection.setAttribute('aria-hidden', 'false');
        }

        if (resultsSection) {
            resultsSection.style.display = 'block';
            resultsSection.setAttribute('aria-hidden', 'false');
        }

        // Show any additional elements that need to be visible
        const enjoyMsg = document.getElementById('enjoy-msg');
        const fpsMailCta = document.getElementById('fps-mail-cta');
        const moreProductsWrapper = document.getElementById('more-products-wrapper');

        if (enjoyMsg) enjoyMsg.style.display = 'block';
        if (fpsMailCta) fpsMailCta.style.display = 'block';
        if (moreProductsWrapper) moreProductsWrapper.style.display = 'block';

        // Initialize any sliders if they exist
        if (window.$ && $('.more-slider').length) {
            try {
                $('.more-slider').slick({
                    slidesToShow: 5,
                    slidesToScroll: 1,
                    arrows: true,
                    dots: false,
                    infinite: false,
                    focusOnSelect: true,
                    draggable: true,
                    swipeToSlide: true,
                    responsive: [
                        {
                            breakpoint: 768,
                            settings: {
                                slidesToShow: 2
                            },
                        },
                        {
                            breakpoint: 1024,
                            settings: {
                                slidesToShow: 3
                            },
                        },
                        {
                            breakpoint: 1400,
                            settings: {
                                slidesToShow: 4
                            },
                        }
                    ]
                });
            } catch (error) {

            }
        }

        // Scroll to CTA section (mobile + desktop aware)
        if (ctaSection) {
            utils.scrollToCTA('.formula-settings-finder__cta');
        } else {
            // Fallback to scrolling to results section if CTA not found
            utils.scrollToElement('.formula-settings-finder__results', -105);
        }
    }

    extractAndPopulateFormulaImages(html) {
        if (!this.formulaReferenceImages) {
            return;
        }

        try {
            // Create a temporary element to parse the HTML
            const tempDiv = document.createElement('div');
            tempDiv.innerHTML = html;

            // First try looking in the formula-images-container (new structure)
            let formulaImagesContainer = tempDiv.querySelector('.formula-images-container');
            let formulaImages = formulaImagesContainer ?
                formulaImagesContainer.querySelectorAll('.formula-image-wrapper') : null;

            // If not found, try the old structure
            if (!formulaImages || formulaImages.length === 0) {
                formulaImages = tempDiv.querySelectorAll('.formula-image-wrapper');
            }

            if (formulaImages && formulaImages.length > 0) {
                // Clear existing images
                this.formulaReferenceImages.innerHTML = '';

                // Add each image to the reference container
                formulaImages.forEach(imageWrapper => {
                    // Only clone the image wrapper and its contents
                    const clone = imageWrapper.cloneNode(true);
                    this.formulaReferenceImages.appendChild(clone);
                });
            } else {
                // Try to find individual images if no .formula-image-wrapper elements are found
                const formulaImgs = tempDiv.querySelectorAll('img.formula-image');

                if (formulaImgs && formulaImgs.length > 0) {
                    // Clear existing images
                    this.formulaReferenceImages.innerHTML = '';

                    // Add each image to the reference container
                    formulaImgs.forEach(img => {
                        const wrapper = document.createElement('div');
                        wrapper.className = 'formula-image-wrapper';
                        wrapper.appendChild(img.cloneNode(true));
                        this.formulaReferenceImages.appendChild(wrapper);
                    });
                } else {
                    // If no images found, display a message
                    this.formulaReferenceImages.innerHTML = '<p>' + this.t('no_reference_images', 'No reference images available for this formula.') + '</p>';
                }
            }

            // Make sure any images in the stage settings wrapper are removed
            if (this.findStageSetting) {
                const stageSettingImgs = this.findStageSetting.querySelectorAll('.formula-image-wrapper, .formula-images-container, .settings-note-wrapper');
                stageSettingImgs.forEach(img => img.remove());
            }
        } catch (error) {
            console.error('Error extracting formula images:', error);
            this.formulaReferenceImages.innerHTML = '<p>Error loading reference images.</p>';
        }
    }

    initializeWarrantyForm() {
        // Get form elements
        this.registerProductForm = document.querySelector('.register-product-form');
        this.reviewForm = document.querySelector('.submit-review-form');
        this.noThanksLinks = document.querySelectorAll('.noThanks');
        this.registerProduct = document.querySelector('.register-product');
        this.submitReview = document.getElementById('submit_review');
        this.formulaProFilter = document.getElementById('formula_pro_filter');
        this.warrantyRegiDiv = document.getElementById('warranty_regi_div');

        // Attach event listeners to forms
        if (this.registerProductForm) {
            this.registerProductForm.addEventListener('submit', this.handleRegisterationSubmit.bind(this));
        }

        if (this.reviewForm) {
            this.reviewForm.addEventListener('submit', this.handleReviewSubmit.bind(this));
        }

        if (this.noThanksLinks && this.noThanksLinks.length > 0) {
            this.noThanksLinks.forEach(link => {
                link.addEventListener('click', this.handleNoThanksClick.bind(this));
            });
        }
    }

    // Handle registration form submission
    handleRegisterationSubmit(event) {
        event.preventDefault();

        // Get active product
        const activeProduct = document.querySelector('.formula-settings-banner__product.active');
        if (!activeProduct) {
            alert('Please select a product first');
            return;
        }

        const productID = activeProduct.getAttribute('data-product-id');
        const productName = activeProduct.getAttribute('data-product-name');

        // Show loading indicator
        const submitContainer = event.target.querySelector('.wholescale-submit-product-registration');
        if (submitContainer) {
            submitContainer.classList.add('active');
        }

        // Get form data
        const formData = new FormData(event.target);
        this.firstName = formData.get('first_name');
        this.lastName = formData.get('last_name');
        this.email = formData.get('email');

        const captcha = document.querySelector('[name="cf-turnstile-response"]')?.value;

        const isValid = validEmail(this.email);
        if (!isValid) {
            utils.showToast(this.t('error_invalid_email', 'Please enter a valid email address'), 'error');
            submitContainer.classList.remove('active');
            return
        }

        const registerationData = {
            email:this.email,
            first: this.firstName,
            last: this.lastName,
            product: productName,
            productId: productID,
            website: this.websiteInput.value,
            is_vip: Boolean(this.userInfo.value),
            opt_in: formData.get('is_subscribed') === 'on',
            captchaToken: captcha
        };
        // Only add black_box if it exists and is not empty
        try {
            if (window.blackBoxStr && window.blackBoxStr.trim() !== '') {
                registerationData.black_box = encodeURIComponent(window.blackBoxStr);
                utils.log('Added blackbox data to registration');
            } else {
                utils.log('BlackBox data is not available, proceeding without it');
            }
        } catch (error) {
            utils.error('Error adding blackbox data:', error);
        }
        this.submitRegisterationForm(registerationData, submitContainer);
    }

    submitRegisterationForm(registerationData, submitContainer) {
        const url = this.warrantyApiUrl;

        fetch(url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(registerationData)
        })
        .then(response => {
            return response.json();
        })
        .then(async res => {
            if (res.success === false) {
                utils.showToast(res.message || "Registration failed", "error");
                if (submitContainer) {
                    submitContainer.classList.remove("active");
                }
                return;
            }
            utils.showToast(res.message, "success");
            const showReview = this.warrantyRegiDiv && this.warrantyRegiDiv.dataset.showReview === 'true';
            if (showReview) {
                this.forceWarrantyFormTransition();
            } else {
                this.skipToFormulaFinder();
            }
            this.selectMarketing(this.websiteInput, 'warranty-optin','warranty_email_form')
            if (submitContainer) {
                submitContainer.classList.remove('active');
            }
        })
        .catch(async error => {
            utils.showToast((error && error.message) || "Registration failed", "error");

            // Skip to formula finder if it's a blackbox error
            const errorMsg = error.message || '';
            if (errorMsg.includes('black_box') || errorMsg.includes('This field may not be blank')) {
                utils.log('Bypassing registration due to blackbox error');
                this.skipToFormulaFinder();
            } else {
                // Show a user-friendly error
            }

            // Remove active class from submit button
            if (submitContainer) {
                submitContainer.classList.remove('active');
            }
        });
    }

    // Handle review form submission
    handleReviewSubmit(event) {
        event.preventDefault();
        // Show loading indicator
        const submitContainer = event.target.querySelector('.wholescale-submit-review');
        if (submitContainer) {
            submitContainer.classList.add('active');
        }
        // Get form data
        const formData = new FormData(event.target);
        const activeProduct = document.querySelector('.formula-settings-banner__product.active');
        const productID = activeProduct.getAttribute('data-product-id');
        const productName = activeProduct.getAttribute('data-product-name');
        const productUrl = activeProduct.getAttribute('data-product-url');
        const emailData = this.registerProductForm.querySelector('[name="email"]').value
        const isValid = validEmail(emailData);
        if (!isValid) {
            utils.showToast(this.t('error_invalid_email', 'Please enter a valid email address'), 'error', '', '');
            submitContainer.classList.remove('active');
            return
        }
        const reviewData = new URLSearchParams({
            website: this.websiteInput.value,
            sku: productID,
            product_title: productName,
            product_url: productUrl,
            display_name: `${this.registerProductForm.querySelector('[name="first_name"]').value} ${this.registerProductForm.querySelector('[name="last_name"]').value}`,
            email: emailData,
            review_content: formData.get('detail'),
            review_title: formData.get('title'),
            review_score: formData.get('rating')
        });

        this.submitReviewForm(reviewData, submitContainer);
    }

    submitReviewForm(reviewData, submitContainer) {
        const url = this.reviewApiUrl;

        fetch(url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/x-www-form-urlencoded'
            },
            body: reviewData.toString()
        })
        .then(response => {
            return response.json();
        })
        .then(data => {
            utils.log('Review response:', data);
            if (data.success === false) {
                utils.showToast(data.message || "Review adding failed", "error");
                if (submitContainer) {
                    submitContainer.classList.remove("active");
                }
                return;
            }
            utils.showToast('Review Submitted Successfully', 'success');
            // Delay transition slightly to allow toast to appear first
            setTimeout(() => {
                // Remove active class from submit button before moving to formula finder
                if (submitContainer) {
                    submitContainer.classList.remove('active');
                }
                this.emailInput.value = reviewData.get('email');

                // Make sure submit button isn't focused before hiding
                if (document.activeElement && submitContainer &&
                    submitContainer.contains(document.activeElement)) {
                    document.activeElement.blur();
                }

                // Now it's okay to hide the warranty div and go to formula finder
                this.skipToFormulaFinder();
            }, 500);
        })
        .catch(error => {
            utils.error('Review Error:', error);

            // Remove active class from submit button
            if (submitContainer) {
                submitContainer.classList.remove('active');
            }
        });
    }

    // Handle No Thanks link click
    handleNoThanksClick(event) {
        event.preventDefault();

        // Blur the link to remove focus
        if (event.target && event.target.blur) {
            event.target.blur();
        }

        this.skipToFormulaFinder();
    }

    // Skip to formula finder function
    skipToFormulaFinder() {
        // First find where we're going to move focus
        const formulaProFilter = this.formulaProFilter || document.getElementById('formula_pro_filter');

        // If we have a formula filter section, focus on it first
        if (formulaProFilter) {
            // Find a good element to focus within the formula filter section
            const focusTarget = formulaProFilter.querySelector('button, [tabindex="0"], a, input, select') || formulaProFilter;

            // Blur any active element to remove focus from form elements
            if (document.activeElement && document.activeElement.blur) {
                document.activeElement.blur();
            }

            // Set tabindex on the target if it's not already focusable
            if (!focusTarget.getAttribute('tabindex')) {
                focusTarget.setAttribute('tabindex', '-1');
            }

            // Move focus to the target element
            focusTarget.focus();

            utils.log('Moved focus to formula finder section before hiding warranty div');
        }

        // Now it's safe to hide the warranty div
        if (this.warrantyRegiDiv) {
            // First remove the show-review class if it exists
            this.warrantyRegiDiv.classList.remove('show-review');

            // Then hide the div and set aria-hidden
            this.warrantyRegiDiv.style.display = 'none';
            this.warrantyRegiDiv.setAttribute('aria-hidden', 'true');
            utils.log('Warranty div hidden with aria-hidden after moving focus');
        }

        // Show formula filter section
        if (formulaProFilter) {
            formulaProFilter.style.display = 'block';
            formulaProFilter.setAttribute('aria-hidden', 'false');

            // Scroll to formula filter section with offset
            utils.scrollToElement('#formula_pro_filter', -105); // Scroll 100px above the element
        }

        // Ensure CTA and results sections remain hidden
        const ctaSection = document.querySelector('.formula-settings-finder__cta');
        if (ctaSection) {
            ctaSection.style.display = 'none';
        }

        const resultsSection = document.querySelector('.formula-settings-finder__results');
        if (resultsSection) {
            resultsSection.style.display = 'none';
        }
    }

    // Force warranty form transition
    forceWarrantyFormTransition() {
        utils.log('Forcing warranty form transition');

        // Check initial state of elements
        const initialWarrantyCss = this.warrantyRegiDiv ? window.getComputedStyle(this.warrantyRegiDiv) : null;
        const initialRegisterCss = this.registerProduct ? window.getComputedStyle(this.registerProduct) : null;
        const initialReviewCss = this.submitReview ? window.getComputedStyle(this.submitReview) : null;

        utils.log('Initial state before transition:', {
            warrantyDisplay: initialWarrantyCss ? initialWarrantyCss.display : 'unknown',
            registerDisplay: initialRegisterCss ? initialRegisterCss.display : 'unknown',
            reviewDisplay: initialReviewCss ? initialReviewCss.display : 'unknown'
        });

        // First, make sure we have the review form element
        if (!this.submitReview) {
            this.submitReview = document.getElementById('submit_review');
            utils.log('Found submit review by ID:', !!this.submitReview);
        }

        // Use setTimeout to ensure this runs after any other code
        setTimeout(() => {
            // 1. Make sure warranty div is visible with !important
            if (this.warrantyRegiDiv) {
                this.warrantyRegiDiv.style.cssText = 'display: block !important; opacity: 1 !important; visibility: visible !important;';
                this.warrantyRegiDiv.removeAttribute('aria-hidden');
                // Add a special class to help with CSS targeting
                this.warrantyRegiDiv.classList.add('show-review');
                utils.log('Force set warrantyRegiDiv display to block !important');
            }

            // 2. Make sure registration form is hidden with !important
            if (this.registerProduct) {
                this.registerProduct.style.cssText = 'display: none !important; visibility: hidden !important;';
                utils.log('Force set registerProduct display to none !important');
            }

            // 3. Make sure review form is visible with !important
            if (this.submitReview) {
                this.submitReview.style.cssText = 'display: block !important; visibility: visible !important; opacity: 1 !important;';
                this.submitReview.classList.add('active');
                utils.log('Force set submitReview display to block !important');

                // Also try to find it by selector and set display directly
                const reviewForms = document.querySelectorAll('.submit-review, #submit_review');
                reviewForms.forEach(form => {
                    form.style.cssText = 'display: block !important; visibility: visible !important; opacity: 1 !important;';
                    form.classList.add('active');
                    utils.log('Force display on additional review form element');
                });
            } else {
                utils.error('Could not find submit review element!');
            }

            // 4. Log the computed styles to verify
            const warrantyCss = this.warrantyRegiDiv ? window.getComputedStyle(this.warrantyRegiDiv) : null;
            const registerCss = this.registerProduct ? window.getComputedStyle(this.registerProduct) : null;
            const reviewCss = this.submitReview ? window.getComputedStyle(this.submitReview) : null;

            utils.log('After initial forcing:', {
                warrantyDisplay: warrantyCss ? warrantyCss.display : 'unknown',
                registerDisplay: registerCss ? registerCss.display : 'unknown',
                reviewDisplay: reviewCss ? reviewCss.display : 'unknown'
            });

            // 5. Double-check after a slight delay to ensure changes took effect
            setTimeout(() => {
                // Try again with even more force
                if (this.warrantyRegiDiv) {
                    // Force through direct DOM manipulation
                    this.warrantyRegiDiv.setAttribute('style', 'display: block !important; opacity: 1 !important; visibility: visible !important;');

                    // Also try jQuery if available
                    if (window.$ && $(this.warrantyRegiDiv).length) {
                        $(this.warrantyRegiDiv).show();
                    }
                }

                if (this.registerProduct) {
                    // Force through direct DOM manipulation
                    this.registerProduct.setAttribute('style', 'display: none !important; visibility: hidden !important;');

                    // Also try jQuery if available
                    if (window.$ && $(this.registerProduct).length) {
                        $(this.registerProduct).hide();
                    }
                }

                if (this.submitReview) {
                    // Force through direct DOM manipulation
                    this.submitReview.setAttribute('style', 'display: block !important; visibility: visible !important; opacity: 1 !important;');
                    this.submitReview.classList.add('active');

                    // Also try jQuery if available
                    if (window.$ && $(this.submitReview).length) {
                        $(this.submitReview).show();
                    }

                    utils.log('Secondary forcing applied to review form');
                } else {
                    // Try to find by ID again and force it
                    const reviewElement = document.getElementById('submit_review');
                    if (reviewElement) {
                        reviewElement.setAttribute('style', 'display: block !important; visibility: visible !important; opacity: 1 !important;');
                        reviewElement.classList.add('active');
                        this.submitReview = reviewElement;
                        utils.log('Found and forced submit_review by ID in secondary check');
                    }
                }

                // Log final state
                const finalWarrantyCss = this.warrantyRegiDiv ? window.getComputedStyle(this.warrantyRegiDiv) : null;
                const finalReviewCss = this.submitReview ? window.getComputedStyle(this.submitReview) : null;

                utils.log('Final state after all attempts:', {
                    warrantyDisplay: finalWarrantyCss ? finalWarrantyCss.display : 'unknown',
                    reviewDisplay: finalReviewCss ? finalReviewCss.display : 'unknown'
                });

                // Force show review form with MutationObserver as a last resort
                if (this.warrantyRegiDiv && this.submitReview) {
                    const observer = new MutationObserver((mutations) => {
                        mutations.forEach((mutation) => {
                            if (mutation.type === 'attributes' && mutation.attributeName === 'style') {
                                // If warranty div style changes, ensure it stays visible
                                if (this.warrantyRegiDiv.style.display === 'none') {
                                    this.warrantyRegiDiv.setAttribute('style', 'display: block !important; opacity: 1 !important; visibility: visible !important;');
                                    utils.log('MutationObserver fixed warranty div visibility');
                                }

                                // Make sure review form stays visible
                                if (this.submitReview.style.display === 'none') {
                                    this.submitReview.setAttribute('style', 'display: block !important; visibility: visible !important; opacity: 1 !important;');
                                    utils.log('MutationObserver fixed review form visibility');
                                }
                            }
                        });
                    });

                    // Start observing warranty div and review form for style changes
                    observer.observe(this.warrantyRegiDiv, { attributes: true });
                    observer.observe(this.submitReview, { attributes: true });

                    // Disconnect after 3 seconds
                    setTimeout(() => observer.disconnect(), 3000);
                }
            }, 200);
        }, 100);
    }

    // Hide sections that should start hidden
    hideInitialSections() {
        // Hide warranty registration until product is selected
        const warrantyRegiDiv = document.getElementById('warranty_regi_div');
        if (warrantyRegiDiv) {
            warrantyRegiDiv.style.display = 'none';
            utils.log('Hidden warranty registration on page load');
        }

        // Always hide CTA section
        const ctaSection = document.querySelector('.formula-settings-finder__cta');
        if (ctaSection) {
            ctaSection.style.display = 'none';
            utils.log('Hidden CTA section on page load');
        }

        // Always hide results section
        const resultsSection = document.querySelector('.formula-settings-finder__results');
        if (resultsSection) {
            resultsSection.style.display = 'none';
            utils.log('Hidden results section on page load');
        }

        // Hide formula filter initially
        const formulaProFilter = document.getElementById('formula_pro_filter');
        if (formulaProFilter) {
            formulaProFilter.style.display = 'none';
            utils.log('Hidden formula filter on page load');
        }

        // Hide the dynamic listing section initially
        const dynamicListingSection = document.querySelector('.section--dynamic-listing');
        if (dynamicListingSection) {
            dynamicListingSection.style.display = 'none';
            utils.log('Hidden dynamic listing section on page load');
        }
    }

    // Setup all barcode scan triggers
    setupScanTriggers() {

        // Find the start_upc button if not already found
        if (!this.startUpcButton) {
            this.startUpcButton = document.getElementById('start_upc');
            if (this.startUpcButton) {
                const startScanBtn = () => {
                    if (!this.validateEmail()) {
                        this.showError(this.t('error_invalid_email', 'Please enter a valid email address'));
                        return;
                    }
                    if (!this.validateLotNumber()) {
                        return;
                    }
                    this.initBarcodeScanner();
                };
                this.startUpcButton.addEventListener('click', startScanBtn);
                this.startUpcButton.addEventListener('keypress', (e) => {
                    if (e.key === 'Enter') startScanBtn();
                });
            }
        }

        // Find and setup all scan trigger elements (including the image)
        const scanTriggers = document.querySelectorAll('.scan-trigger, .image-wrapper.scan-trigger');

        scanTriggers.forEach((trigger, index) => {

            // Remove existing event listeners if any (to prevent duplicates)
            const newTrigger = trigger.cloneNode(true);
            trigger.parentNode.replaceChild(newTrigger, trigger);

            const startScan = () => {
                if (!this.validateEmail()) {
                    this.showError(this.t('error_invalid_email', 'Please enter a valid email address'));
                    return;
                }
                if (!this.validateLotNumber()) {
                    return;
                }
                this.initBarcodeScanner();
            };

            newTrigger.addEventListener('click', startScan);

            newTrigger.addEventListener('keypress', (e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                    startScan();
                }
            });
        });
    }
}

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
    new FormulaSettings();
    initLotNumberModal();
    initLotNumberInput();
});

function initLotNumberModal() {
    const modal = document.getElementById('lot-number-modal');
    const openBtn = document.getElementById('lot-number-help-btn');
    if (!modal || !openBtn) return;

    const closeBtn = modal.querySelector('.lot-number-modal-close');

    // Move to body so position:fixed covers full viewport regardless of parent transforms
    document.body.appendChild(modal);

    function closeModal() {
        modal.hidden = true;
        openBtn.focus();
    }

    openBtn.addEventListener('click', () => {
        modal.hidden = false;
        closeBtn && closeBtn.focus();
    });

    closeBtn && closeBtn.addEventListener('click', closeModal);

    // Click on the dark overlay area (outside dialog) closes modal
    modal.addEventListener('click', (e) => {
        if (e.target === modal) closeModal();
    });

    document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && !modal.hidden) closeModal();
    });
}

function initLotNumberInput() {
    const input = document.getElementById('lot_number_input');
    const checkIcon = document.getElementById('lot-input-check');
    const wrapper = document.querySelector('.lot-number-wrapper');
    if (!input || !wrapper) return;

    // --- Visibility logic ---
    // Lot input is shown for Formula Pro Advanced *and* WiFi (both behave the same now).
    function isAdvancedOrWifi(el) {
        const url  = (el?.dataset?.productUrl  || '').toLowerCase();
        const name = (el?.dataset?.productName || '').toLowerCase();
        if (url.includes('wifi') || name.includes('wifi')) return true;
        return url.includes('formula-pro-advanced')
            || name.includes('formula pro® advanced')
            || name.includes('formula pro advanced');
    }

    function checkLotVisibility() {
        const path = window.location.pathname.toLowerCase();
        const onAdvancedPage = path.includes('formula-pro-advanced') || path.includes('wifi');
        const activeProduct  = document.querySelector('.formula-settings-banner__product.active');
        const show = onAdvancedPage || (activeProduct && isAdvancedOrWifi(activeProduct));
        wrapper.style.display = show ? '' : 'none';
    }

    // Hide by default, then evaluate
    wrapper.style.display = 'none';
    checkLotVisibility();

    // Re-evaluate whenever a product card is clicked
    document.querySelectorAll('.formula-settings-banner__product').forEach(p => {
        p.addEventListener('click', () => setTimeout(checkLotVisibility, 0));
    });

    // Also watch for class changes (covers keyboard / programmatic selection)
    const banner = document.querySelector('.formula-settings-finder__banner-content-products');
    if (banner) {
        new MutationObserver(checkLotVisibility).observe(banner, { subtree: true, attributeFilter: ['class'] });
    }

    // --- Input behavior: 2–14 chars, auto-uppercase, no completion styling ---
    const MAX_LEN = 14;
    input.setAttribute('maxlength', String(MAX_LEN));
    input.setAttribute('autocapitalize', 'characters');
    input.setAttribute('placeholder', 'Enter your lot number');
    if (checkIcon) checkIcon.style.display = 'none';
    input.classList.remove('lot-input-valid');

    input.addEventListener('input', () => {
        const upper = input.value.toUpperCase().slice(0, MAX_LEN);
        if (input.value !== upper) {
            const pos = input.selectionStart;
            input.value = upper;
            try { input.setSelectionRange(pos, pos); } catch (e) {}
        }
        // Clear lot error once the user reaches the minimum length (2)
        if (input.value.length >= 2) {
            wrapper.querySelectorAll('.lot-number-error').forEach(e => e.remove());
        }
    });

    input.getLotPrefix = () => input.value;
}

/* ==========================================================================
   Image preview lightbox — self-contained, no modal framework dependency.
   Any element with [data-image-preview] opens a full-screen preview on click.
   Ported from baby-brezza-v2's <image-preview-modal> but rewritten to run
   standalone in this older theme (no ModalComponent base class available).
   ========================================================================== */
(function () {
    if (window.__formulaImagePreviewInit) return;
    window.__formulaImagePreviewInit = true;

    let modal = null;

    function build() {
        if (modal) return modal;
        modal = document.createElement('div');
        modal.className = 'image-preview-modal';
        modal.setAttribute('hidden', '');
        modal.innerHTML = `
            <div class="image-preview__overlay" data-preview-close></div>
            <button type="button" class="image-preview__close" data-preview-close aria-label="Close">
                <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true" focusable="false">
                    <path d="M6 6l12 12M18 6L6 18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
                </svg>
            </button>
            <div class="image-preview__inner" data-preview-close>
                <figure class="image-preview__figure">
                    <img class="image-preview__img" alt="" />
                    <figcaption class="image-preview__caption" hidden></figcaption>
                </figure>
            </div>`;
        document.body.appendChild(modal);

        modal.addEventListener('click', (e) => {
            if (e.target.closest('[data-preview-close]') && !e.target.closest('.image-preview__img')) {
                close();
            }
        });
        return modal;
    }

    function open(src, { alt = '', caption = '' } = {}) {
        if (!src) return;
        const el = build();
        const img = el.querySelector('.image-preview__img');
        const cap = el.querySelector('.image-preview__caption');
        img.src = src;
        img.alt = alt;
        cap.textContent = caption || '';
        cap.hidden = !caption;
        el.removeAttribute('hidden');
        // force reflow so the transition runs
        void el.offsetWidth;
        el.setAttribute('active', '');
        document.body.style.overflow = 'hidden';
        document.addEventListener('keydown', onKeydown);
    }

    function close() {
        if (!modal) return;
        modal.removeAttribute('active');
        document.body.style.overflow = '';
        document.removeEventListener('keydown', onKeydown);
        const onEnd = () => {
            modal.setAttribute('hidden', '');
            modal.removeEventListener('transitionend', onEnd);
        };
        modal.addEventListener('transitionend', onEnd);
        // fallback in case transitionend doesn't fire
        setTimeout(() => { if (modal && !modal.hasAttribute('active')) modal.setAttribute('hidden', ''); }, 350);
    }

    function onKeydown(e) {
        if (e.key === 'Escape') close();
    }

    document.addEventListener('click', (e) => {
        const trigger = e.target.closest('[data-image-preview]');
        if (!trigger) return;
        e.preventDefault();

        let src = trigger.getAttribute('data-full');
        if (!src) {
            if (trigger.tagName === 'IMG') {
                src = trigger.currentSrc || trigger.src;
            } else if (trigger.tagName === 'A') {
                src = trigger.getAttribute('href');
            } else {
                const innerImg = trigger.querySelector('img');
                if (innerImg) src = innerImg.currentSrc || innerImg.src;
            }
        }
        if (!src) return;

        open(src, {
            alt: trigger.getAttribute('data-alt') || trigger.getAttribute('alt') || (trigger.querySelector('img')?.alt ?? ''),
            caption: trigger.getAttribute('data-caption') || '',
        });
    });
})();
